import {
  APPLY_FILTER,
  APPLY_SORT,
  DISMISS_FILTER_PILL,
  RESET_FILTER,
  SET_COLUMN,
  SET_DATA,
  SET_SELECTION,
  TOGGLE_ALL,
  TOGGLE_ITEM,
} from './Table.action';
import {
  ActionType,
  ColumnProps,
  SelectedFilterProps,
  State,
} from './Table.interface';
import {
  applyFilter,
  applySort,
  changeFilter,
  setSelectedItems,
  stringCellComparator,
} from './Table.utils';

export const initialState: State = {
  columns: {
    key: 'columns',
    items: [],
  },
  data: [],
  filterValues: {},
  sortColumn: '',
  sortDirection: 0,
  selectedItems: [],
};

const ALL = 'All';

export const setHeaderColumns = (columns: ColumnProps) => {
  return columns.items.map(item =>
    item.sort
      ? {
          ...item,
          cellComparator: stringCellComparator,
        }
      : {
          ...item,
        },
  );
};

const getColumns = ({ columns }: State) => ({
  columns,
  headerColumns: setHeaderColumns(columns),
});

const getData = (state: State) => {
  const { filterKeys, filterValues } = getFilterValues(state);
  const { headerColumns } = getColumns(state);
  return filterKeys.length > 0
    ? {
        data: applySort(
          state.sortColumn,
          state.sortDirection,
          headerColumns,
          applyFilter(state.data, filterValues),
        ),
        allRows: state.data,
      }
    : {
        data: applySort(
          state.sortColumn,
          state.sortDirection,
          headerColumns,
          state.data,
        ),
        allRows: state.data,
      };
};

const getFilterValues = ({ filterValues }: State) => ({
  filterValues,
  filterKeys: Object.keys(filterValues).filter(
    key => filterValues[key] !== 'All',
  ),
});

const getSortData = ({ sortColumn, sortDirection }: State) => ({
  sortColumn,
  sortDirection,
});

const getSelectedItems = ({ selectedItems }: State) => selectedItems;

const tableReducer = (state = initialState, { type, payload }: ActionType) => {
  switch (type) {
    case SET_COLUMN:
      return {
        ...state,
        columns: payload.columns,
      };

    case SET_DATA:
      return {
        ...state,
        data: payload.data,
      };

    case APPLY_FILTER:
      return {
        ...state,
        filterValues: changeFilter(
          payload as SelectedFilterProps,
          state.filterValues,
        ),
      };

    case RESET_FILTER:
      return {
        ...state,
        filterValues: {},
      };

    case DISMISS_FILTER_PILL:
      return {
        ...state,
        filterValues: changeFilter(
          { [payload as string]: ALL },
          state.filterValues,
        ),
      };

    case APPLY_SORT: {
      const { sortColumn, sortDirection } = payload;
      return {
        ...state,
        sortColumn,
        sortDirection,
      };
    }

    case SET_SELECTION:
      return {
        ...state,
        selectedItems: payload,
      };

    case TOGGLE_ITEM: {
      const { checked, itemKey } = payload;
      return {
        ...state,
        selectedItems: setSelectedItems(state.selectedItems, itemKey, checked),
      };
    }

    case TOGGLE_ALL: {
      const { checked } = payload;
      const { data } = getData(state);
      return {
        ...state,
        selectedItems: checked ? data.map(({ key }) => key.toString()) : [],
      };
    }
    default:
      return state;
  }
};

export {
  getColumns,
  getData,
  getFilterValues,
  getSelectedItems,
  getSortData,
  tableReducer as reducer,
};
